import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import CloseIcon from '@material-ui/icons/Close';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import Button from 'UI/button';
import SimpleMDE from 'react-simplemde-editor';
import 'easymde/dist/easymde.min.css';
import * as _ from 'lodash';
import axios from 'axios';
import { marked } from 'marked';
import { baseURL } from 'utils/apiEndpoints';
import { TIMEOUT } from 'utils/consts';

class RequestQuotationModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      emailContent: this.generateEmailContent(),
      isSending: false,
    };
    
    // SimpleMDE options
    this.editorOptions = {
      spellChecker: false,
      placeholder: 'Enter your quotation request...',
      status: false,
      toolbar: [
        'bold', 'italic', 'heading', '|',
        'quote', 'unordered-list', 'ordered-list', '|',
        'link', '|',
        'preview', 'side-by-side', 'fullscreen', '|',
        'guide',
      ],
    };
  }

  componentDidUpdate(prevProps) {
    const { isOpen } = this.props;
    if (isOpen && !prevProps.isOpen) {
      // Regenerate email content when modal opens
      this.setState({ emailContent: this.generateEmailContent() });
    }
  }

  generateEmailContent = () => {
    const { aiItineraryData } = this.props;
    const { itineraryParts = [] } = aiItineraryData;

    let content = 'Please revert with price for the following:\n\n';

    itineraryParts.forEach((part, index) => {
      if (part.type === 'STAY' && part.stayData && !part.costPrice) {
        const { stayData } = part;
        content += `Hotel ${stayData.stayName}:\n`;
        
        if (!_.isEmpty(stayData.rooms)) { 
          stayData.rooms.forEach((room, index1) => {
            content += `• Room ${index1 + 1}: ${room.roomName}\n`;
            content += `  - PAX: ${room.adults} Adult${room.adults > 1 ? 's' : ''}${room.children > 0 ? `, ${room.children} child${room.children > 1 ? 'ren' : ''}` : ''}\n`;
            content += `  - Dates: ${room.checkInDate} to ${room.checkOutDate}\n\n`;
          });
        }              
      }
      
      if (part.type === 'ACTIVITY' && part.activityData && !part.costPrice) {
        const { activityData } = part;
        content += `Activity: ${activityData.experienceName}:\n`;
        
        if (part.startDate) {
          content += `  - Date: ${part.startDate}`;
          content += '\n';
        }
        
        const pax = [];
        if (activityData.adults) {
          pax.push(`${activityData.adults} Adult${activityData.adults > 1 ? 's' : ''}`);
        }
        if (activityData.children) {
          pax.push(`${activityData.children} child${activityData.children > 1 ? 'ren' : ''}`);
        }
        if (activityData.infants) {
          pax.push(`${activityData.infants} infant${activityData.infants > 1 ? 's' : ''}`);
        }
        if (pax.length > 0) {
          content += `  - PAX: ${pax.join(', ')}\n`;
        }
        
        content += '\n';
      }
    });

    return content;
  };

  handleClose = () => {
    const { closePricingModal } = this.props;
    closePricingModal();
  };

  handleContentChange = (value) => {
    this.setState({ emailContent: value });
  };

  handleSendQuoteRequest = async () => {
    const {
      showSnackbar, closePricingModal, itineraryId, aiItineraryData, updateItineraryData,
    } = this.props;
    const { emailContent } = this.state;

    this.setState({ isSending: true });

    try {
      // Convert markdown to HTML
      const emailBody = marked(emailContent);

      // Make POST API call
      const response = await axios({
        url: `${baseURL}itineraries/${itineraryId}/sendQuotationEmail`,
        method: 'post',
        data: {
          emailBody,
        },
        timeout: TIMEOUT,
      });

      const quoteThreadId = response.data?.quoteThreadId;

      if (quoteThreadId) {
        updateItineraryData({
          ...aiItineraryData,
          quoteThreadId,
        });
        showSnackbar('Quote request sent successfully', 'success');
        closePricingModal();
      }
    } catch (error) {
      console.error('Error sending quote request:', error);
      showSnackbar('Failed to send quote request. Please try again.', 'error');
    } finally {
      this.setState({ isSending: false });
    }
  };

  render() {
    const { classes, isOpen } = this.props;
    const { emailContent, isSending } = this.state;

    return (
      <Dialog
        open={isOpen}
        onClose={this.handleClose}
        maxWidth="md"
        fullWidth
        classes={{
          paper: classes.dialogPaper,
        }}
      >
        <div className={classes.modalContainer}>
          {/* Header */}
          <div className={classes.header}>
            <div className={classes.titleContainer}>
              <DescriptionOutlinedIcon className={classes.headerIcon} />
              <span className={classes.title}>Request Quotation</span>
            </div>
            <CloseIcon 
              className={classes.closeIcon} 
              onClick={this.handleClose}
            />
          </div>

          {/* Content */}
          <div className={classes.content}>
            {/* Instructions */}
            <div className={classes.instructionsText}>
              Edit the email below and hit send - to request a quotation from our supply partners over email. 
              Responses can take up to 48 hours—escalate to your Mentor/TL if delayed. 
              Once received, update the itinerary & prices by speaking to AI agent as per the quotation.
            </div>

            {/* Email Content Editor */}
            <div className={classes.editorContainer}>
              <SimpleMDE
                value={emailContent}
                onChange={this.handleContentChange}
                options={this.editorOptions}
              />
            </div>

            {/* Send Button */}
            <Button
              variant="contained"
              className={classes.sendButton}
              onClick={this.handleSendQuoteRequest}
              disabled={isSending}
            >
              {isSending ? 'Sending ...' : 'Send Quote Request'}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  dialogPaper: {
    borderRadius: 12,
    width: 800,
    margin: 16,
  },
  modalContainer: {
    background: theme.colors.white,
    borderRadius: 12,
    fontFamily: 'Sofia-pro, sans-serif',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  headerIcon: {
    fontSize: 20,
    marginTop: 2,
    color: theme.colors.primary,
    marginRight: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    color: '#32302F',
    fontFamily: 'Sofia-pro, sans-serif',
  },
  closeIcon: {
    fontSize: 24,
    color: '#666666',
    cursor: 'pointer',
    '&:hover': {
      color: '#333333',
    },
  },
  content: {
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  instructionsText: {
    fontSize: 14,
    color: '#666666',
    lineHeight: 1.6,
    fontFamily: 'Sofia-pro, sans-serif',
  },
  editorContainer: {    
    borderRadius: 8,
    marginTop: '20px',
    '& .EasyMDEContainer': {
      fontFamily: 'Sofia-pro, sans-serif',
    },
    '& .CodeMirror': {
      border: '1px solid #E0E0E0',
      borderRadius: 8,
      fontSize: 14,
      fontFamily: 'Sofia-pro, sans-serif',
      minHeight: 300,
      backgroundColor: '#FAFAFA',
    },
    '& .editor-toolbar': {
      border: '1px solid #E0E0E0',
      borderBottom: 'none',
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      backgroundColor: '#FFFFFF',
    },
    '& .CodeMirror-scroll': {
      minHeight: 300,
      maxHeight: 350,
      overflowY: 'auto',
    },
  },
  sendButton: {
    backgroundColor: '#0090B5',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 24,
    height: 36,
    marginTop: 20,
    padding: '0 40px',
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'Sofia-pro, sans-serif',
    alignSelf: 'flex-start',
    '&:hover': {
      backgroundColor: '#007A94',
    },
    '&:disabled': {
      backgroundColor: '#DDDDDD',
      cursor: 'not-allowed',
    },
  },
});

RequestQuotationModal.propTypes = {
  classes: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  closePricingModal: PropTypes.func.isRequired,
  aiItineraryData: PropTypes.object.isRequired,
  showSnackbar: PropTypes.func.isRequired,
  itineraryId: PropTypes.string.isRequired,
  updateItineraryData: PropTypes.func.isRequired,
};

export default withStyles(styles)(RequestQuotationModal);
